import { useMemo, type ReactNode } from 'react';

import logo from '../../../asset/images/planomatic-red-logo.png';

const HERO_CDN_BASE = 'https://login-assets.app.planomatic.com';
const HERO_COUNT = 16;
const heroImages = Array.from(
  { length: HERO_COUNT },
  (_, i) => `${HERO_CDN_BASE}/hero-${String(i + 1).padStart(2, '0')}.jpg`,
);

interface AuthHeroLayoutProps {
  children: ReactNode;
  /** Tailwind max-width class for the card column, e.g. `max-w-[600px]`. */
  cardMaxWidth: string;
  ariaLabelledBy?: string;
}

/**
 * Full-bleed random hero background with a centered floating frosted-glass
 * card. Shared by the registration and additional-details pages so the
 * hero + scrim + card + logo aren't duplicated. One hero image is chosen at
 * random per mount.
 */
const AuthHeroLayout = ({
  children,
  cardMaxWidth,
  ariaLabelledBy,
}: AuthHeroLayoutProps) => {
  const heroImage = useMemo(
    () => heroImages[Math.floor(Math.random() * heroImages.length)],
    [],
  );

  return (
    // min-h-dvh, not min-h-screen: 100vh overshoots on iOS Safari while the
    // URL bar is visible, which pushes the centered card down under the bar
    // (roadmap Known hazard -- closed here per the "whichever unit touches the
    // component" rule).
    <main aria-labelledby={ariaLabelledBy} className="relative min-h-dvh w-full">
      {/* Full-bleed hero + diagonal scrim (decorative). */}
      <img
        src={heroImage}
        loading="eager"
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(120deg,rgba(0,0,0,0.40)_0%,rgba(0,0,0,0.28)_30%,rgba(0,0,0,0.12)_60%,rgba(0,0,0,0)_100%)]"
      />

      {/* Scroll stage: centers the card when it fits, top-aligns + scrolls when tall. */}
      <div className="absolute inset-0 flex items-start justify-center overflow-y-auto p-4 sm:p-6">
        <div className={`my-auto w-full ${cardMaxWidth}`}>
          <div className="rounded-[14px] border border-white/55 bg-background/60 p-[22px_20px] text-[#212529] sm:p-[30px_34px] shadow-[0_24px_60px_rgba(24,39,75,0.30),0_8px_20px_rgba(24,39,75,0.16)] backdrop-blur-[18px] backdrop-saturate-[1.15]">
            <img src={logo} alt="" aria-hidden className="mx-auto mb-5 h-[38px]" />
            {children}
          </div>
        </div>
      </div>
    </main>
  );
};

export default AuthHeroLayout;
